import { MapContainer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import { useEffect, useMemo, useState } from "react";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { TILES, TileManager, BaseTileLayer, ZoomInfo, MAP_MAX_ZOOM } from "@/components/MapTiles";
import { tripFlagIcon } from "@/lib/mapIcons";
import { CATEGORY_META, RENDER_ORDER, catColor, catLabel } from "@/lib/caltracRoad";
import { AlertTriangle, Info, MapPinned, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROAD_LAYERS = ["dark", "satellite", "osm", "georep", "georep_sat"];
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Pacific/Noumea", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

function FitBounds({ bbox }) {
  const map = useMap();
  useEffect(() => {
    if (bbox && bbox.length === 2) {
      map.fitBounds(bbox, { padding: [40, 40], maxZoom: 16 });
    }
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [bbox, map]);
  return null;
}

export default function CaltracRoadMap({ vehicleId, qs }) {
  const [layer, setLayer] = useState("dark");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vehicleId) { setData(null); setError(null); return; }
    setLoading(true); setError(null);
    api.get(`/reports/speed-driving/road-map?${qs}`)
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => {
        setData(null);
        const d = e?.response?.data?.detail;
        setError(typeof d === "string" ? d : "Impossible de charger la carte routière pour cette période.");
      })
      .finally(() => setLoading(false));
  }, [vehicleId, qs]);

  const segments = useMemo(() => {
    const segs = (data?.segments || []).filter((s) => (s.positions || []).length >= 2);
    return [...segs].sort((a, b) => (RENDER_ORDER[a.ckey] || 1) - (RENDER_ORDER[b.ckey] || 1));
  }, [data]);

  const presentCats = useMemo(() => {
    const cov = data?.coverage || {};
    return Object.keys(CATEGORY_META).filter((k) => (cov[k] || 0) > 0);
  }, [data]);

  if (!vehicleId) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="road-map-no-vehicle">
        <MapPinned className="w-6 h-6 mx-auto mb-2 brand-teal" />
        Sélectionnez un <span className="font-semibold">véhicule précis</span> pour afficher sa carte routière CALTRAC.
      </div>
    );
  }

  const cp = data?.coverage_pct || {};
  const cov = data?.coverage || {};
  const ends = segments.length ? [segments[0].positions[0], segments[segments.length - 1].positions.slice(-1)[0]] : null;

  return (
    <div className="space-y-4" data-testid="caltrac-road-map">
      {/* Avertissements honnêteté */}
      {(data?.warnings || []).length > 0 && (
        <div className="rounded-lg border border-[#F5821F]/30 bg-[#F5821F]/10 p-3 space-y-1.5" data-testid="road-map-warnings">
          {data.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <AlertTriangle className="w-4 h-4 brand-orange mt-0.5 shrink-0" />
              <p>{w}</p>
            </div>
          ))}
        </div>
      )}

      {/* Erreur de chargement (ex. période au-delà de la limite) */}
      {error && (
        <div className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 p-3 flex items-start gap-2 text-xs" data-testid="road-map-error">
          <AlertTriangle className="w-4 h-4 text-[#EF4444] mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {!error && data?.available && !data?.empty && (
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="w-4 h-4 brand-teal mt-0.5 shrink-0" />
          <p data-testid="road-map-source">
            Source des limites : <span className="font-semibold">{data?.dataset?.source || "—"}</span>
            {" "}({data?.is_official ? "officielle / VMA validée" : "OSM indicative, NON officielle"})
            {data?.dataset?.source_date ? ` · ${data.dataset.source_date}` : ""}
            {data?.dataset?.license ? ` · ${data.dataset.license}` : ""}
          </p>
        </div>
        <a href={`${BACKEND_URL}/api/reports/speed-driving/road-map.pdf?${qs}`} target="_blank" rel="noreferrer"
          data-testid="road-map-export-pdf"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#F5821F] hover:bg-[#e0741a] text-white text-xs font-bold px-3 py-2">
          <FileText className="w-4 h-4" /> PDF carte
        </a>
      </div>
      )}

      {loading && <p className="text-sm text-muted-foreground" data-testid="road-map-loading">Chargement de la carte…</p>}

      {!loading && !error && data?.empty && (
        <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="road-map-empty">
          Aucun point GPS exploitable sur la période pour ce véhicule.
        </div>
      )}

      {!loading && data?.available && !data?.empty && (
        <div className="grid lg:grid-cols-[1fr_280px] gap-4">
          <div className="relative rounded-xl overflow-hidden border border-border" style={{ height: "62vh", minHeight: 420 }}>
            <div className="absolute top-3 right-3 z-[500] flex gap-1 glass rounded-md p-1" data-testid="road-map-layers">
              {ROAD_LAYERS.map((k) => (
                <button key={k} type="button" onClick={() => setLayer(k)} data-testid={`road-map-layer-${k}`}
                  className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${layer === k ? "bg-[#17A2B8] text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  {TILES[k]?.label || k}
                </button>
              ))}
            </div>
            <MapContainer center={[-22.2758, 166.458]} zoom={12} maxZoom={MAP_MAX_ZOOM} zoomControl={false}
              style={{ height: "100%", width: "100%", background: "#09090b" }}>
              <FitBounds bbox={data.bbox} />
              <BaseTileLayer layer={layer} />
              <TileManager layer={layer} setLayer={setLayer} />
              <ZoomInfo layer={layer} position="bottomright" />
              {segments.map((s, i) => (
                <Polyline key={i} positions={s.positions}
                  pathOptions={{ color: catColor(s.ckey), weight: s.ckey === "vma_over" ? 6 : 4, opacity: 0.92, dashArray: s.ckey === "gps_only" ? "5 7" : undefined }}>
                  <Popup>
                    <div style={{ minWidth: 170 }} data-testid={`road-seg-popup-${i}`}>
                      <b>{catLabel(s.ckey)}</b>
                      <div style={{ fontSize: 11, marginTop: 4 }}>
                        <div>Route : {s.road || "—"}</div>
                        {s.limit != null && <div>Limite : {s.limit} km/h {!data.is_official && s.ckey !== "vs" ? "(OSM indicative)" : ""}</div>}
                        <div>Vitesse max : {s.max_speed} km/h</div>
                        <div>Mesures : {s.n}</div>
                        <div style={{ opacity: 0.7 }}>{fmtDT(s.start)} → {fmtDT(s.end)}</div>
                        {s.ckey === "ambiguous" && <div style={{ color: "#A855F7" }}>Zone non analysée (ouvrage superposé / parallèles)</div>}
                      </div>
                    </div>
                  </Popup>
                </Polyline>
              ))}
              {ends && (
                <>
                  <Marker position={ends[0]} icon={tripFlagIcon("start")}><Popup>Début du trajet</Popup></Marker>
                  <Marker position={ends[1]} icon={tripFlagIcon("end")}><Popup>Fin du trajet</Popup></Marker>
                </>
              )}
            </MapContainer>
          </div>

          <div className="space-y-4">
            {/* Légende */}
            <div className="rounded-xl border border-border bg-card p-3" data-testid="road-map-legend">
              <p className="text-xs font-bold mb-2">Légende (fiabilité de la limite)</p>
              <div className="space-y-1.5">
                {presentCats.map((k) => (
                  <div key={k} className="flex items-center gap-2 text-[11px]" data-testid={`road-legend-${k}`}>
                    <span className="w-4 h-1.5 rounded-full shrink-0" style={{ background: CATEGORY_META[k].color }} />
                    <span className="text-muted-foreground flex-1">{CATEGORY_META[k].label}</span>
                    <span className="data font-bold">{k === "vma" ? Math.max(0, (cov.vma || 0) - (cov.vma_over || 0)) : (cov[k] || 0)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Couverture du trajet */}
            <div className="rounded-xl border border-border bg-card p-3" data-testid="road-map-coverage">
              <p className="text-xs font-bold mb-2">Classement des points sur ce trajet</p>
              <p className="text-[10px] text-muted-foreground mb-2 italic">Total des catégories = 100 %. Ce n'est ni une couverture VMA, ni une couverture du réseau global.</p>
              <div className="space-y-1 text-[11px]">
                <Row label="Points analysés" value={cov.points || 0} />
                <Row label="VMA confirmée" value={`${cp.vma || 0}%`} sub={`${cov.vma || 0} pts · ${cov.vma_over || 0} dépass.`} />
                <Row label="VS supposée (non réglementaire)" value={`${cp.vs || 0}%`} sub={`${cov.vs || 0} pts · jamais un excès`} />
                <Row label="Limite OSM indicative" value={`${cp.osm || 0}%`} sub={`${cov.osm || 0} pts`} />
                <Row label="Sans limite connue" value={`${cp.no_limit || 0}%`} sub={`${cov.no_limit || 0} pts`} />
                <Row label="GPS seul / hors réseau" value={`${cp.gps_only || 0}%`} sub={`${cov.gps_only || 0} pts`} />
                <Row label="Échangeur/ambigu (exclu)" value={`${cp.ambiguous || 0}%`} sub={`${cov.ambiguous || 0} pts`} />
                <Row label="Limite non interprétable" value={`${cp.unsupported || 0}%`} sub={`${cov.unsupported || 0} pts`} />
              </div>
            </div>

            {/* Épisodes d'excès réglementaire (VMA confirmée) */}
            <div className="rounded-xl border border-border bg-card p-3" data-testid="road-map-episodes">
              <p className="text-xs font-bold mb-2">Dépassements de VMA confirmée</p>
              {(data.episodes || []).length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  Aucun dépassement de VMA confirmée exploitable. {data.is_official ? "" : "Impossible de conclure à « zéro excès » sans VMA officielle."}
                </p>
              ) : (
                <div className="space-y-1.5">
                  {data.episodes.slice(0, 20).map((e, i) => (
                    <div key={i} className="text-[11px] border-t border-border/60 pt-1.5 first:border-0 first:pt-0" data-testid={`road-episode-${i}`}>
                      <div className="font-semibold">{e.road || "route"} · <span className="text-[#EF4444]">{e.max_speed} km/h</span> (limite {e.limit}, +{e.excess})</div>
                      <div className="text-muted-foreground">{fmtDT(e.start)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value, sub }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right"><span className="data font-bold">{value}</span>{sub && <span className="block text-[10px] text-muted-foreground">{sub}</span>}</span>
  </div>
);
